import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";

import { ApiError, apiGet, apiPost } from "./api/client";
import type { Me } from "./api/types";
import { ErrorBand } from "./components/ErrorBand";
import { Skeleton } from "./components/Skeleton";

const SESSION_KEY = ["session"] as const;

/** Who is signed in, or undefined. A 401 is an answer, not an error — it is
 *  how the server says "nobody", so it must not be retried or surfaced. */
export function useSession() {
  const query = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await apiGet<Me>("/auth/me");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  return {
    me: query.data ?? undefined,
    loading: query.isLoading,
    // A failure that is NOT a 401. The queryFn turns 401 into null, because
    // "nobody is signed in" is an answer; anything reaching here means the
    // server is down or erroring, which is a different thing to tell someone.
    unavailable: query.isError,
  };
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      apiPost<Me>("/auth/login", credentials),
    onSuccess: (me) => queryClient.setQueryData(SESSION_KEY, me),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost("/auth/logout"),
    // Everything cached was read as this person. Clear it rather than leaving
    // another account's data on screen behind a login form.
    onSuccess: () => queryClient.clear(),
  });
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading, unavailable } = useSession();
  const location = useLocation();
  // Not null: that made the app's very first paint a blank white page, and
  // client.ts has no request timeout, so a hung /auth/me stayed white forever.
  if (loading) return <Skeleton rows={6} />;
  if (unavailable) {
    return (
      <ErrorBand
        message="Could not reach the server."
        hint="This is not a sign-in problem — the service is unreachable or erroring. Reload in a moment."
      />
    );
  }
  if (!me) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}
